import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, CreditCard, ShoppingCart, RefreshCw, Scale, AlertTriangle, HelpCircle, Mail } from 'lucide-react';

const TermsOfSalePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-semibold">REVELIAE</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl mb-4">
              <FileText className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Conditions Générales de Vente</h1>
            <p className="text-gray-500">Dernière mise à jour : 25 février 2026</p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Article 1 */}
            <section className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <span className="text-emerald-600 font-bold">1</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Objet et champ d'application</h2>
              </div>
              <p className="text-gray-600 mb-4">
                Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre :
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-gray-700"><strong>REVELIAE</strong></p>
                <p className="text-gray-600">Portail de services ésotériques en ligne</p>
                <p className="text-gray-600">Email : contact@reveliae.com</p>
              </div>
              <p className="text-gray-600">
                Et toute personne physique ou morale (« le Client ») souscrivant à un abonnement ou effectuant 
                un achat sur le site reveliae.com.
              </p>
            </section>

            {/* Article 2 - Services */}
            <section className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <span className="text-emerald-600 font-bold">2</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Description des services</h2>
              </div>
              <p className="text-gray-600 mb-4">REVELIAE propose les services suivants :</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-violet-50 rounded-xl">
                  <h3 className="font-bold text-violet-900 mb-2">LUMINA (Gratuit)</h3>
                  <p className="text-sm text-violet-700">Analyse chiromancie par IA</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-xl">
                  <h3 className="font-bold text-purple-900 mb-2">NUMEROLOGIA (Premium)</h3>
                  <p className="text-sm text-purple-700">Analyses numérologiques complètes</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl">
                  <h3 className="font-bold text-amber-900 mb-2">ASTROZODIAC (Premium)</h3>
                  <p className="text-sm text-amber-700">Astrologie et horoscopes personnalisés</p>
                </div>
                <div className="p-4 bg-indigo-50 rounded-xl">
                  <h3 className="font-bold text-indigo-900 mb-2">MYSTERIAE (Premium)</h3>
                  <p className="text-sm text-indigo-700">Tirages et interprétations de Tarot</p>
                </div>
              </div>
            </section>

            {/* Article 3 - Prix */}
            <section className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="h-6 w-6 text-emerald-500" />
                <h2 className="text-xl font-bold text-gray-900">3. Prix et modalités de paiement</h2>
              </div>
              <p className="text-gray-600 mb-4">Les prix sont indiqués en euros TTC :</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-3 rounded-tl-lg">Offre</th>
                      <th className="text-left p-3">Prix TTC</th>
                      <th className="text-left p-3 rounded-tr-lg">Durée</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr className="border-b">
                      <td className="p-3 font-medium">Essai REVELIAE</td>
                      <td className="p-3">1,99 €</td>
                      <td className="p-3">7 jours</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-3 font-medium">NUMEROLOGIA Premium</td>
                      <td className="p-3">3,99 €/mois</td>
                      <td className="p-3">Mensuel</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-3 font-medium">ASTROZODIAC Premium</td>
                      <td className="p-3">3,99 €/mois</td>
                      <td className="p-3">Mensuel</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-3 font-medium">MYSTERIAE Premium</td>
                      <td className="p-3">3,99 €/mois</td>
                      <td className="p-3">Mensuel</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">REVELIAE Intégral</td>
                      <td className="p-3">9,99 €/mois</td>
                      <td className="p-3">Mensuel</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-gray-600 mt-4">
                <strong>Moyens de paiement acceptés :</strong> Carte bancaire (Visa, Mastercard) via Stripe, PayPal.
              </p>
            </section>

            {/* Article 4 - Abonnements */}
            <section className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <ShoppingCart className="h-6 w-6 text-blue-500" />
                <h2 className="text-xl font-bold text-gray-900">4. Abonnements et renouvellement</h2>
              </div>
              <ul className="list-disc list-inside text-gray-600 space-y-3">
                <li>Les abonnements sont conclus pour une durée indéterminée avec facturation mensuelle.</li>
                <li>L'abonnement est renouvelé automatiquement à chaque échéance sauf résiliation.</li>
                <li>La résiliation peut être effectuée à tout moment depuis votre espace client.</li>
                <li>La résiliation prend effet à la fin de la période en cours déjà payée.</li>
                <li>Aucun remboursement prorata temporis n'est effectué en cas de résiliation anticipée.</li>
              </ul>
            </section>

            {/* Article 5 - Rétractation */}
            <section className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <RefreshCw className="h-6 w-6 text-amber-500" />
                <h2 className="text-xl font-bold text-gray-900">5. Droit de rétractation</h2>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <p className="text-amber-800">
                  <strong>Délai de rétractation :</strong> Conformément à l'article L221-18 du Code de la consommation, 
                  vous disposez d'un délai de 14 jours à compter de la souscription pour exercer votre droit de rétractation 
                  sans avoir à justifier de motif.
                </p>
              </div>
              <p className="text-gray-600 mb-4">
                Pour exercer ce droit, envoyez un email à <a href="mailto:support@reveliae.com" className="text-blue-600 hover:underline">support@reveliae.com</a> avec :
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Votre nom et prénom</li>
                <li>Votre adresse email de compte</li>
                <li>La date de souscription</li>
                <li>La mention claire de votre volonté de vous rétracter</li>
              </ul>
              <p className="text-gray-600 mt-4">
                Le remboursement sera effectué dans un délai de 14 jours suivant la réception de votre demande, 
                via le même moyen de paiement utilisé lors de la transaction initiale.
              </p>
            </section>

            {/* Article 6 - Responsabilité */}
            <section className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="h-6 w-6 text-rose-500" />
                <h2 className="text-xl font-bold text-gray-900">6. Limitation de responsabilité</h2>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 mb-4">
                <p className="text-rose-800">
                  <strong>Avertissement :</strong> Les services proposés par REVELIAE sont fournis à titre de 
                  divertissement et de développement personnel. Ils ne constituent en aucun cas des conseils 
                  médicaux, juridiques, financiers ou professionnels.
                </p>
              </div>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>REVELIAE ne saurait être tenu responsable des décisions prises sur la base des interprétations fournies.</li>
                <li>Les analyses générées par intelligence artificielle sont fournies sans garantie de résultat.</li>
                <li>En cas de problème de santé, consultez un professionnel médical qualifié.</li>
              </ul>
            </section>

            {/* Article 7 - Propriété intellectuelle */}
            <section className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="h-6 w-6 text-indigo-500" />
                <h2 className="text-xl font-bold text-gray-900">7. Propriété intellectuelle</h2>
              </div>
              <p className="text-gray-600">
                L'ensemble des contenus présents sur le site REVELIAE (textes, images, logos, illustrations, 
                logiciels, etc.) sont protégés par le droit de la propriété intellectuelle. Toute reproduction, 
                représentation, modification ou exploitation non autorisée est strictement interdite.
              </p>
            </section>

            {/* Article 8 - Litiges */}
            <section className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <HelpCircle className="h-6 w-6 text-cyan-500" />
                <h2 className="text-xl font-bold text-gray-900">8. Médiation et litiges</h2>
              </div>
              <p className="text-gray-600 mb-4">
                En cas de litige, le Client peut recourir gratuitement au service de médiation suivant :
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-gray-700 font-medium">Médiateur de la consommation</p>
                <p className="text-gray-600">CM2C - Centre de Médiation de la Consommation de Conciliateurs de Justice</p>
                <p className="text-gray-600">www.cm2c.net</p>
              </div>
              <p className="text-gray-600">
                <strong>Droit applicable :</strong> Les présentes CGV sont soumises au droit français. 
                En cas de litige non résolu à l'amiable, les tribunaux français seront seuls compétents.
              </p>
            </section>

            {/* Contact */}
            <section className="p-6 bg-white rounded-2xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="h-6 w-6 text-purple-500" />
                <h2 className="text-xl font-bold text-gray-900">9. Contact</h2>
              </div>
              <p className="text-gray-600 mb-4">Pour toute question relative à ces CGV :</p>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-gray-700 font-medium">REVELIAE</p>
                <p className="text-gray-600">Email : <a href="mailto:support@reveliae.com" className="text-blue-600 hover:underline">support@reveliae.com</a></p>
                <p className="text-gray-600">Service client : Du lundi au vendredi, 9h-18h</p>
              </div>
            </section>
          </div>

          {/* Back link */}
          <div className="mt-12 text-center">
            <Link to="/" className="text-gray-500 hover:text-gray-700">
              ← Retour à l'accueil
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default TermsOfSalePage;
